import React, {
    useContext,
    useEffect,
    useMemo,
    useCallback
} from 'react';
import { useValidation } from 'gw-portals-validation-react';
import { publish, JUTRO_TOPICS } from '@jutro/events';
import { ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { TranslatorContext } from '@jutro/locale';
import { SgDateUtil } from 'saga-modules';
import moment from 'moment';
import _ from 'lodash';
import datePickerMetadata from './metadata/DatePicker.metadata.json5';
import yearOnlyMetadata from './metadata/YearOnlyDatePicker.metadata.json5';
import monthOnlyMetadata from './metadata/MonthOnlyDatePicker.metadata.json5';
import dayMonthYearInputsMetadata from './metadata/DayMonthYearInputs.metadata.json5';
import monthYearPickerMetadata from './metadata/MonthYearPicker.metadata.json5';
import SgDatePicker from './sg-date-picker/SgDatePicker';
import SgMonthYearPicker from './sg-month-year-picker/SgMonthYearPicker';
import SgDateInputs from './sg-date-inputs/SgDateInputs';
import './SgDate.messages';

import SgDateInputTypes from './SgDateInputTypes';

function SgDate(props) {
    const {
        id,
        customId,
        minDate,
        maxDate,
        calculateDayToSet,
        modelVM,
        updateData,
        showErrors,
        datePickerTypeDesktop,
        datePickerTypeMobile,
        path,
        label,
        secondaryLabel,
        requiredFieldValidationMessage,
        onValidate: onValidateParent,
        onClickFn,
        showValidationIcon,
        disabled,
        disabledManualInput,
        additionalDatePickerText,
        autoCompleteYear
    } = props;

    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useContext(TranslatorContext);

    useEffect(() => {
        return () => {
            if (onValidateParent) {
                // workaround when component was not valid but is no longer needed (hidden)
                onValidateParent(true, id);
            }
        };
    }, []);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    const getLocalDate = (date) => {
        return date ? {
            year: date.getFullYear()
                .toString(),
            month: date.getMonth(),
            monthViewValue: (date.getMonth() + 1).toString(),
            day: date.getDate()
                .toString()
        } : undefined;
    };

    const pickerType = useMemo(() => {
        if (breakpoint === 'desktop') {
            return datePickerTypeDesktop || SgDateInputTypes.DATEPICKER;
        }
        return datePickerTypeMobile || datePickerTypeDesktop || SgDateInputTypes.DAY_MONTH_YEAR_INPUTS;
    }, [breakpoint, datePickerTypeDesktop, datePickerTypeDesktop]);

    const writeValue = useCallback((value) => {
        const newModelVM = viewModelService.clone(modelVM);
        if (pickerType === SgDateInputTypes.YEAR_ONLY) {
            if (_.get(modelVM, path).year.aspects.valid) {
                let date = new Date(value, 0, 1);
                date = date && getLocalDate(date);
                _.set(newModelVM, path, date);
            }
        } else if (pickerType === SgDateInputTypes.MONTH_DROPDOWN) {
            _.set(newModelVM, path, { month: Number.parseInt(value, 10) });
        } else if (pickerType === SgDateInputTypes.DAY_MONTH_YEAR_INPUTS || pickerType === SgDateInputTypes.MONTH_YEAR_PICKER
            || pickerType === SgDateInputTypes.MONTH_YEAR_INPUTS) {
            _.set(newModelVM, path, _.assign(_.get(newModelVM, `${path}.value`), value));
        } else {
            _.set(newModelVM, path, value);
        }

        if (pickerType === SgDateInputTypes.MONTH_YEAR_PICKER || pickerType === SgDateInputTypes.MONTH_YEAR_INPUTS) {
            const date = _.get(newModelVM, path);
            if (date.value.year !== undefined && date.value.month !== undefined) {
                const dayToSet = calculateDayToSet ? calculateDayToSet(date.value) : moment()
                    .format('DD');
                _.set(newModelVM, `${path}.day.value`, dayToSet);
            }
        }

        updateData(newModelVM);
        // set adobe tagging
        publish(JUTRO_TOPICS.VALUE_CHANGED, {
            id: customId || id,
            value: _.get(newModelVM, `${path}.value`),
        });
    }, [updateData, modelVM, pickerType]);

    const metadata = useMemo(() => {
        let metadataValue;

        switch (pickerType) {
            case SgDateInputTypes.DATEPICKER: {
                metadataValue = datePickerMetadata.componentContent;
                break;
            }
            case SgDateInputTypes.YEAR_ONLY: {
                metadataValue = yearOnlyMetadata.componentContent;
                break;
            }
            case SgDateInputTypes.MONTH_DROPDOWN: {
                metadataValue = monthOnlyMetadata.componentContent;
                break;
            }
            case SgDateInputTypes.MONTH_YEAR_INPUTS:
            case SgDateInputTypes.DAY_MONTH_YEAR_INPUTS: {
                metadataValue = dayMonthYearInputsMetadata.componentContent;
                break;
            }
            case SgDateInputTypes.MONTH_YEAR_PICKER: {
                metadataValue = monthYearPickerMetadata.componentContent;
                break;
            }
            default: {
                break; // picker type should always be defined
            }
        }

        return metadataValue;
    }, [pickerType]);

    const overrideProps = {};
    const defaultProps = {
        path,
        label,
        secondaryLabel,
        showValidationIcon,
        disabled,
        disabledManualInput
    };
    const customComponentCommonProps = {
        modelVM,
        onValidate,
        showErrors,
        writeValue,
        customId: customId || id,
        minDate,
        maxDate,
        additionalDatePickerText
    };

    switch (pickerType) {
        case SgDateInputTypes.DATEPICKER: {
            overrideProps.datePicker = {
                ...defaultProps,
                ...customComponentCommonProps,
                value: SgDateUtil.toDate(_.get(modelVM, `${path}.value`)),
                isDateValid: isComponentValid
            };
            break;
        }
        case SgDateInputTypes.YEAR_ONLY: {
            overrideProps.datePickerYearField = {
                ...defaultProps,
                value: (_.get(modelVM, path).year && _.get(modelVM, path).year.value) || '',
            };
            break;
        }
        case SgDateInputTypes.MONTH_DROPDOWN: {
            overrideProps.monthSelectPicker = {
                ...defaultProps,
                requiredFieldValidationMessage: requiredFieldValidationMessage,
                value: _.get(modelVM, path).month && !_.isNil(_.get(modelVM, path).month.value) ? _.get(modelVM, path).month.value : '',
            };
            break;
        }
        case SgDateInputTypes.MONTH_YEAR_PICKER: {
            overrideProps.monthYearPicker = {
                ...defaultProps,
                ...customComponentCommonProps,
                value: SgDateUtil.toDate(_.get(modelVM, `${path}.value`))
            };
            break;
        }
        case SgDateInputTypes.MONTH_YEAR_INPUTS:
        case SgDateInputTypes.DAY_MONTH_YEAR_INPUTS: {
            overrideProps.dateInputs = {
                ...defaultProps,
                ...customComponentCommonProps,
                value: _.get(modelVM, `${path}.value`),
                showValidationIcon: _.get(modelVM, `${path}.aspects.valid`),
                autoCompleteYear,
                isDateValid: isComponentValid
            };
            break;
        }
        default: {
            break; // picker type should always be defined
        }
    }

    if (pickerType === SgDateInputTypes.MONTH_YEAR_INPUTS) {
        overrideProps.dateInputs.hideDay = true;
    }

    const resolvers = {
        resolveCallbackMap: {
            changeYear: (value) => {
                _.set(modelVM, path, {
                    year: value
                });
                writeValue(value); // handle model change if valid
            },
            onValidate: onValidate
        },
        resolveComponentMap: {
            SgDatePicker: SgDatePicker,
            SgMonthYearPicker: SgMonthYearPicker,
            SgDateInputs: SgDateInputs
        }
    };

    return (
        <div
            className={!isComponentValid && showErrors ? 'invalidDateFieldContainer' : ''}
            onClick={onClickFn}
            role="button"
            tabIndex="-1"
            onKeyUp={() => {}}
            id={`${id}_container`}
        >
            {pickerType !== SgDateInputTypes.READONLY && pickerType !== SgDateInputTypes.READONLY_WITH_LABEL
                ? (
                    <ViewModelForm
                        uiProps={metadata}
                        model={modelVM}
                        onValueChange={writeValue}
                        showErrors={showErrors}
                        onValidationChange={onValidate}
                        overrideProps={overrideProps}
                        callbackMap={resolvers.resolveCallbackMap}
                        componentMap={resolvers.resolveComponentMap}
                    />
                )
                : (
                    <>
                        {pickerType === SgDateInputTypes.READONLY_WITH_LABEL && (
                            <p>{translator(label)}</p>
                        )}
                        <span>{SgDateUtil.toLongDate(_.get(modelVM, path).value)}</span>
                    </>
                )
            }
        </div>
    );
}

export default SgDate;
